import { useEffect, useState } from "react";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { PhotoThumb } from "../equipment/shared/PhotoThumb";
import DieselItemForm from "./DieselItemForm";
import { HoEntryTag } from "./shared/HoManualEntryFields";

const fmt = (n) => (n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 1 });
const fmtTime = (t) => (t ? new Date(t).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—");

export function DieselStatsGrid({ day }) {
  const stats = [
    { label: "Previous Stock", value: day.previous_stock, testId: "diesel-stat-previous" },
    { label: "Received Today", value: day.received_today, testId: "diesel-stat-received" },
    { label: "Issued Today", value: day.issued_today, testId: "diesel-stat-issued" },
    { label: "Total Stock", value: day.total_stock, testId: "diesel-stat-total" },
    { label: "Balance", value: day.balance, testId: "diesel-stat-balance", highlight: true },
    { label: "Total Received Till Date", value: day.total_received_till_date, testId: "diesel-stat-total-received" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="diesel-stats-grid">
      {stats.map((s) => (
        <div key={s.label} className="rounded-lg border border-slate-200 bg-white p-3">
          <p className="text-[11px] uppercase tracking-wide text-slate-400">{s.label}</p>
          <p
            data-testid={s.testId}
            className={`font-mono text-lg font-semibold ${s.highlight ? (s.value < 0 ? "text-red-600" : "text-emerald-700") : "text-slate-800"}`}
          >
            {fmt(s.value)} L
          </p>
        </div>
      ))}
    </div>
  );
}

export default function DieselDayDetail({ dayId, onClose, onChanged }) {
  const [day, setDay] = useState(null);
  const [form, setForm] = useState(null);

  useEffect(() => {
    client
      .get(`/diesel-register/day/${dayId}`)
      .then((res) => setDay(res.data))
      .catch((err) => {
        toast.error(getErrorMessage(err));
        onClose();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayId]);

  const applyUpdate = (updated) => {
    setDay(updated);
    if (updated.negative_balance_warning) {
      toast.warning("Diesel balance has gone negative for this site. Please verify the entries.");
    }
    onChanged?.();
  };

  const remove = async (kind, itemId) => {
    if (!window.confirm("Remove this line-item? This cannot be undone.")) return;
    try {
      const res = await client.delete(`/diesel-register/day/${dayId}/${kind}/${itemId}`);
      applyUpdate(res.data);
      toast.success("Entry removed");
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  if (!day) {
    return (
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent data-testid="diesel-day-detail-loading">
          <p className="py-8 text-center text-slate-400">Loading...</p>
        </DialogContent>
      </Dialog>
    );
  }

  const editable = day.editable;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="diesel-day-detail">
        <DialogHeader>
          <DialogTitle>
            {day.display_label} — {new Date(day.date).toLocaleDateString("en-IN")}
          </DialogTitle>
        </DialogHeader>

        <DieselStatsGrid day={day} />

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700">Received ({day.received_items.length})</h3>
            {editable && (
              <Button size="sm" data-testid="diesel-add-received" onClick={() => setForm({ kind: "received", initial: null })} className="bg-amber-600 hover:bg-amber-700">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add Received
              </Button>
            )}
          </div>
          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <Table data-testid="diesel-received-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Quantity</TableHead>
                  <TableHead>Remarks</TableHead>
                  <TableHead>By</TableHead>
                  <TableHead>Photos</TableHead>
                  {editable && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {day.received_items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-4 text-slate-400 text-sm">
                      No received entries.
                    </TableCell>
                  </TableRow>
                ) : (
                  day.received_items.map((it) => (
                    <TableRow key={it.item_id} data-testid={`diesel-received-row-${it.item_id}`}>
                      <TableCell className="text-xs">{fmtTime(it.time)}</TableCell>
                      <TableCell className="font-mono">{fmt(it.quantity)} L</TableCell>
                      <TableCell className="text-xs max-w-[160px] truncate">{it.remarks || "—"}</TableCell>
                      <TableCell className="text-xs">{it.entered_by_name || it.entered_by}{it.ho_entry && <HoEntryTag />}</TableCell>
                      <TableCell>
                        <div className="flex gap-1.5">
                          {(it.photos || []).map((_, i) => (
                            <PhotoThumb key={i} path={`/diesel-register/day/${dayId}/received/${it.item_id}/photo/${i}`} alt="received" testId={`diesel-received-photo-${it.item_id}-${i}`} size="h-10 w-10" />
                          ))}
                        </div>
                      </TableCell>
                      {editable && (
                        <TableCell className="text-right space-x-1.5">
                          <Button size="sm" variant="outline" data-testid={`diesel-received-edit-${it.item_id}`} onClick={() => setForm({ kind: "received", initial: it })}>
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="destructive" data-testid={`diesel-received-delete-${it.item_id}`} onClick={() => remove("received", it.item_id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700">Issued ({day.issued_items.length})</h3>
            {editable && (
              <Button size="sm" data-testid="diesel-add-issued" onClick={() => setForm({ kind: "issued", initial: null })} className="bg-amber-600 hover:bg-amber-700">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add Issued
              </Button>
            )}
          </div>
          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <Table data-testid="diesel-issued-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Equipment</TableHead>
                  <TableHead>Quantity</TableHead>
                  <TableHead>Reading</TableHead>
                  <TableHead>By</TableHead>
                  <TableHead>Remarks</TableHead>
                  <TableHead>Photos</TableHead>
                  {editable && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {day.issued_items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-4 text-slate-400 text-sm">
                      No issued entries.
                    </TableCell>
                  </TableRow>
                ) : (
                  day.issued_items.map((it) => (
                    <TableRow key={it.item_id} data-testid={`diesel-issued-row-${it.item_id}`}>
                      <TableCell className="text-xs">{fmtTime(it.time)}</TableCell>
                      <TableCell className="text-xs">
                        {it.equipment_label}{" "}
                        {it.equipment_type === "hired" && <Badge className="bg-sky-600 ml-1">Hired</Badge>}
                      </TableCell>
                      <TableCell className="font-mono">{fmt(it.quantity)} L</TableCell>
                      <TableCell className="font-mono text-xs">{fmt(it.current_reading)}</TableCell>
                      <TableCell className="text-xs">{it.entered_by_name || it.entered_by}{it.ho_entry && <HoEntryTag />}</TableCell>
                      <TableCell className="text-xs max-w-[140px] truncate">{it.remarks || "—"}</TableCell>
                      <TableCell>
                        <div className="flex gap-1.5">
                          {(it.photos || []).map((_, i) => (
                            <PhotoThumb key={i} path={`/diesel-register/day/${dayId}/issued/${it.item_id}/photo/${i}`} alt="issued" testId={`diesel-issued-photo-${it.item_id}-${i}`} size="h-10 w-10" />
                          ))}
                        </div>
                      </TableCell>
                      {editable && (
                        <TableCell className="text-right space-x-1.5">
                          <Button size="sm" variant="outline" data-testid={`diesel-issued-edit-${it.item_id}`} onClick={() => setForm({ kind: "issued", initial: it })}>
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="destructive" data-testid={`diesel-issued-delete-${it.item_id}`} onClick={() => remove("issued", it.item_id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {form && (
          <DieselItemForm
            kind={form.kind}
            dayId={dayId}
            site={day.site}
            initial={form.initial}
            onClose={() => setForm(null)}
            onSaved={(updated) => {
              setForm(null);
              applyUpdate(updated);
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
